import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { includes, isFunction, isNaN, join, map, times } from 'lodash-es';

import { getMerchantColor, isMobileSelector } from 'redux/selectors/global';
import { IFormOtpProps } from 'components/containers/OTP/OTPContent';
import { isValidOtp } from 'utilities/form/validations';
import { KeyboardEventKeys, BULLET_UNICODE_CHAR } from 'consts';

const allowedKeyCodes: Partial<KeyboardEventKeys>[] = [
  KeyboardEventKeys.ENTER,
  KeyboardEventKeys.BACKSPACE,
  KeyboardEventKeys.LEFT,
  KeyboardEventKeys.ARROW_LEFT,
  KeyboardEventKeys.RIGHT,
  KeyboardEventKeys.ARROW_RIGHT
];

interface IProps extends IFormOtpProps {
  name: string;
  onChange: (value: string, index: number) => void;
  isMasked?: boolean;
  fixedWidth?: number;
  isOtp?: boolean;
  onFocus?: (...params: any) => any;
}

const OTP = React.forwardRef(
  (
    {
      name,
      otpDisabled,
      codeLength,
      onChange,
      errorMessage,
      handleOtpChange,
      isMasked,
      fixedWidth,
      isOtp,
      onFocus,
      ...rest
    }: IProps,
    ref
  ) => {
    const isMobile = useSelector(isMobileSelector);
    const merchantColor = useSelector(getMerchantColor);
    const initialCodeArr: Array<string> = times(codeLength, () => '');

    const [code, setCode] = useState(initialCodeArr);

    const regexForNumbersOnly = /\D/;
    const codeWrapperRef = useRef<HTMLDivElement>(null);

    const formattedCode = (passCode: Array<string>): string => join(passCode, '');
    const focusElement = (htmlElement: HTMLInputElement | undefined) =>
      htmlElement?.focus();

    const updatePassCode = (index: number, val: string) => {
      if (val === KeyboardEventKeys.SPACE || isNaN(val)) return;

      const curCode = code;
      curCode[index] = val;
      setCode(curCode);

      if (onChange) onChange(formattedCode(curCode), index);

      if (isMobile && isOtp) {
        const allInputs: HTMLCollection | undefined = codeWrapperRef?.current?.children;
        const element = allInputs?.[index] as HTMLInputElement;
        const nextElementSibling = element.nextElementSibling as HTMLInputElement | null;

        if (nextElementSibling) {
          focusElement(nextElementSibling);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!includes(allowedKeyCodes, e.key) && regexForNumbersOnly.test(e.key)) {
        e.stopPropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (errorMessage) handleOtpChange({ error: errorMessage });

      const allInputs: HTMLCollection | undefined = codeWrapperRef?.current?.children;
      const newValue = (e.target as HTMLInputElement).value;
      if (
        (!includes(allowedKeyCodes, e.key) && regexForNumbersOnly.test(e.key)) ||
        e.key === KeyboardEventKeys.TAB ||
        e.key === KeyboardEventKeys.SPACE ||
        isNaN(newValue)
      ) {
        e.stopPropagation();
        e.preventDefault();
        return false;
      }

      if (e.key === KeyboardEventKeys.ENTER) {
        if (isValidOtp(codeLength)(code) && onChange) onChange(formattedCode(code), index);
      } else if (
        e.key === KeyboardEventKeys.BACKSPACE ||
        e.key === KeyboardEventKeys.LEFT ||
        e.key === KeyboardEventKeys.ARROW_LEFT
      ) {
        if (index > 0) {
          const element = allInputs?.[index - 1] as HTMLInputElement;
          focusElement(element);
        }
      } else if (
        e.key === KeyboardEventKeys.RIGHT ||
        e.key === KeyboardEventKeys.ARROW_RIGHT ||
        (index < codeLength - 1 && newValue)
      ) {
        const element = allInputs?.[index + 1] as HTMLInputElement;
        focusElement(element);
      }
    };

    const wrapperProps = {
      'data-testid': name || 'otp',
      ref: codeWrapperRef,
      ...rest
    };

    return (
      <Wrapper {...wrapperProps}>
        {map(code, (val: string, index: number) => (
          <SingleBox
            ref={index === 0 ? (ref as React.RefObject<HTMLInputElement>) : null}
            value={isMasked && val ? BULLET_UNICODE_CHAR : val}
            autoFocus={index === 0}
            key={index}
            pattern="\d*"
            onChange={({ target }) => updatePassCode(index, target.value)}
            maxLength={1}
            disabled={otpDisabled}
            onKeyDown={handleKeyDown}
            onKeyUp={e => handleKeyUp(e, index)}
            onFocus={({ target }) => {
              if (isFunction(onFocus)) {
                onFocus();
              }
              return target?.select();
            }}
            data-testid="otp-digits"
            autoComplete="off"
            isMasked={isMasked}
            errorMessage={errorMessage}
            merchantColor={merchantColor}
            boxWidth={fixedWidth}
          />
        ))}
      </Wrapper>
    );
  }
);

export default OTP;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
  gap: 1rem;
`;

const SingleBox = styled.input<{
  errorMessage: string;
  isMasked?: boolean;
  merchantColor: string;
  boxWidth?: number;
}>`
  outline: none;
  width: ${({ boxWidth }) => (boxWidth ? `${boxWidth}rem` : '100%')};
  height: 5.4rem;
  text-align: center;
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.colors.disabled};
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid
    ${({ theme, errorMessage }) =>
    errorMessage ? theme.colors.error : theme.colors.surface3};

  font-size: ${({ theme, isMasked }) => {
    const { large, extraLarge } = theme.fonts.size;

    if (isMasked) {
      const { isMobile } = theme.view;
      return isMobile ? '3rem' : extraLarge;
    }

    return large;
  }};

  &:focus {
    color: ${({ theme }) => theme.colors.link.primary};
    border: 0.1rem solid ${({ merchantColor }) => merchantColor};
  }
`;
